import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Btn, KPI, KPIRow, Loading, Msg, Section, TextInput, panelPost } from '../kit';
import { Modal } from '../email/emailKit';
import { Pill, str } from '../ipintel/shared';
import { InlineEdit, OpenLink, SHEET_LABELS, Toggle, fmtInt } from './shared';

/**
 * Labels & carousel — the university website labels (the short name used in
 * the homepage ticker AND as the {university} segment of every page URL),
 * the separate "show in the homepage carousel" switch, and each university
 * page's URL. It reads the same GET universities/sheet as the Universities
 * tab and writes through the same actions, so the two screens always agree:
 *  - label:    POST db/set_university_label {code, label}
 *  - carousel: POST universities/set_carousel {code, on}
 *  - fill:     POST universities/fill_labels {overwrite}
 * The server rebuilds the homepage doc (~20 s) and the page URLs after each.
 *
 * CarouselToggle and FillLabelsButton are also used by the Universities tab.
 */

export type UniRow = {
  code: string; name: string; website_label: string; website_label_source: 'admin' | 'seed' | 'missing' | string;
  carousel?: boolean | null; page_path?: string | null; techs?: number; country?: string;
};

/** A university can be on the carousel only with a label; `carousel` is the stored switch. */
export const onCarousel = (r: UniRow) => !!str(r.website_label).trim() && r.carousel === true;

export const CarouselToggle: React.FC<{
  row: UniRow; onSaved: (text: string) => void; onError: (text: string) => void;
}> = ({ row, onSaved, onError }) => {
  const [busy, setBusy] = useState(false);
  const hasLabel = !!str(row.website_label).trim();
  const on = onCarousel(row);
  const flip = async (next: boolean) => {
    setBusy(true);
    const { error } = await panelPost('universities', 'set_carousel', { code: row.code, on: next });
    setBusy(false);
    if (error) { onError(`${row.code}: ${error}`); return; }
    onSaved(next
      ? `${row.website_label} is on the homepage carousel (live within ~20 s).`
      : `${row.website_label || row.code} is off the homepage carousel (within ~20 s).`);
  };
  return (
    <Toggle on={on} busy={busy} disabled={!hasLabel} onChange={flip} label={`Homepage carousel — ${row.code}`}
      title={!hasLabel ? 'Add a website label first — the carousel shows the label'
        : on ? 'On the homepage carousel — click to take it off' : 'Not on the homepage carousel — click to add it'} />
  );
};

type Plan = { code: string; name: string; from: string; to: string; kind: 'new' | 'seed' | 'overwrite' };

/** "Fill missing labels from list" — confirm lists exactly what will be written first. */
export const FillLabelsButton: React.FC<{
  rows: UniRow[]; onDone: (text: string, kind: 'ok' | 'error') => void;
}> = ({ rows, onDone }) => {
  const [open, setOpen] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const byCode = useMemo(() => Object.fromEntries(rows.map(r => [r.code, r])), [rows]);
  const plan: Plan[] = useMemo(() => Object.entries(SHEET_LABELS).flatMap(([code, label]): Plan[] => {
    const r = byCode[code];
    if (!r) return [];
    const cur = str(r.website_label);
    if (r.website_label_source === 'admin') {
      return overwrite && cur !== label ? [{ code, name: r.name, from: cur, to: label, kind: 'overwrite' }] : [];
    }
    return [{ code, name: r.name, from: cur, to: label, kind: r.website_label_source === 'seed' ? 'seed' : 'new' }];
  }), [byCode, overwrite]);
  const notInCorpus = Object.keys(SHEET_LABELS).filter(c => !byCode[c]);
  const fresh = plan.filter(p => p.kind === 'new').length;

  const run = async () => {
    setBusy(true);
    const { data, error } = await panelPost('universities', 'fill_labels', { overwrite });
    setBusy(false);
    setOpen(false);
    if (error) { onDone(`Fill labels failed: ${error}`, 'error'); return; }
    const filled: string[] = Array.isArray(data?.filled) ? data.filled : [];
    const skipped: string[] = Array.isArray(data?.skipped) ? data.skipped : [];
    const names = filled.map(c => c.replace(/^UTECH-/, ''));
    const list = names.length > 10 ? `${names.slice(0, 10).join(', ')} and ${names.length - 10} more` : names.join(', ');
    onDone(`Filled ${filled.length} label${filled.length === 1 ? '' : 's'}${filled.length ? ` (${list})` : ''}`
      + `${skipped.length ? `; skipped ${skipped.length} that already had one` : ''}. The homepage and the page URLs rebuild in the background.`, 'ok');
  };

  return (
    <>
      <Btn small onClick={() => setOpen(true)} title="Fill the website labels from the university list for every university that has none stored">
        Fill missing labels from list
      </Btn>
      {open && (
        <Modal title="Fill website labels from the list?" onClose={() => !busy && setOpen(false)}
          footer={<>
            <Btn onClick={() => setOpen(false)} disabled={busy}>Cancel</Btn>
            <Btn kind="primary" onClick={run} disabled={busy || plan.length === 0}>
              {busy ? 'Writing…' : `Write ${plan.length} label${plan.length === 1 ? '' : 's'}`}
            </Btn>
          </>}>
          {plan.length === 0 ? (
            <p>Nothing to fill — every university in the list already has a stored label.</p>
          ) : (
            <>
              <p className="text-xs text-grayscale-1100">
                These will be stored. {fresh > 0 && <b>{fresh} newly labelled universit{fresh === 1 ? 'y starts' : 'ies start'} OFF the homepage carousel</b>}
                {fresh > 0 && ' — switch them on one by one.'} Labels also become the university part of every page URL.
              </p>
              <div className="max-h-72 overflow-auto rounded-lg border border-grayscale-300">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-grayscale-200">
                    <tr>
                      <th className="text-left px-2 py-1.5">University</th>
                      <th className="text-left px-2 py-1.5">Now</th>
                      <th className="text-left px-2 py-1.5">Will be</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.map(p => (
                      <tr key={p.code} className="border-t border-grayscale-200">
                        <td className="px-2 py-1"><div className="text-grayscale-1300">{p.name}</div><div className="font-mono text-[10px] text-grayscale-1100">{p.code}</div></td>
                        <td className="px-2 py-1">
                          {p.kind === 'new' ? <span className="text-amber-700">no label</span>
                            : p.kind === 'seed' ? <span className="italic text-grayscale-1100">{p.from} (seed)</span>
                            : <span>{p.from || <i className="text-grayscale-1100">blank (off)</i>}</span>}
                        </td>
                        <td className="px-2 py-1 font-medium">
                          {p.to}
                          {p.kind === 'seed' && p.from === p.to && <span className="text-grayscale-1100 font-normal"> · made official</span>}
                          {p.kind === 'overwrite' && <Pill tone="warn">overwrite</Pill>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
            <input type="checkbox" checked={overwrite} onChange={e => setOverwrite(e.target.checked)} />
            Also overwrite labels an admin already set (normally left alone)
          </label>
          {notInCorpus.length > 0 && (
            <div className="text-[11px] text-grayscale-1100">Not in the corpus, skipped: {notInCorpus.join(', ')}</div>
          )}
        </Modal>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
const LabelsCarousel: React.FC<{
  sheet: { data: any; loading: boolean; error: string; reload: () => void };
}> = ({ sheet }) => {
  const [q, setQ] = useState('');
  const [onlyNoLabel, setOnlyNoLabel] = useState(false);
  const [onlyCarousel, setOnlyCarousel] = useState(false);
  const [flash, setFlash] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const later = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (later.current) clearTimeout(later.current); }, []);

  const rows: UniRow[] = Array.isArray(sheet.data?.rows) ? sheet.data.rows : [];
  const view = useMemo(() => {
    let out = rows;
    const n = q.trim().toLowerCase();
    if (n) out = out.filter(r => [r.name, r.code, r.website_label, r.page_path, r.country].some(v => str(v).toLowerCase().includes(n)));
    if (onlyNoLabel) out = out.filter(r => !str(r.website_label).trim());
    if (onlyCarousel) out = out.filter(onCarousel);
    return out;
  }, [rows, q, onlyNoLabel, onlyCarousel]);

  /** Reload now, and once more when the background URL rebuild has had time to finish. */
  const refresh = () => {
    sheet.reload();
    if (later.current) clearTimeout(later.current);
    later.current = setTimeout(() => sheet.reload(), 20000);
  };
  const ok = (text: string) => { setFlash({ kind: 'ok', text }); refresh(); };
  const fail = (text: string) => setFlash({ kind: 'error', text });
  const saveLabel = async (code: string, label: string) => {
    const { error } = await panelPost('db', 'set_university_label', { code, label });
    if (error) return error;
    ok(label.trim()
      ? `Label for ${code} saved. Its page URLs move to the new name (old ones redirect) within ~20 s.`
      : `Label for ${code} cleared — off the carousel; its URLs fall back to the university name.`);
    return '';
  };

  const labelled = rows.filter(r => str(r.website_label).trim()).length;
  const carousel = rows.filter(onCarousel).length;

  if (sheet.loading && !sheet.data) return <Loading label="Reading the universities…" />;
  return (
    <div className="flex flex-col gap-4">
      {sheet.error && <Msg kind="error">{sheet.error}</Msg>}
      {flash && (
        <div className="flex items-center gap-2">
          <div className="flex-1"><Msg kind={flash.kind}>{flash.text}</Msg></div>
          <button type="button" onClick={() => setFlash(null)} className="text-xs text-grayscale-1100 underline">dismiss</button>
        </div>
      )}
      <KPIRow>
        <KPI label="Universities" value={fmtInt(rows.length)} />
        <KPI label="With a label" value={fmtInt(labelled)} />
        <KPI label="Without a label" value={fmtInt(rows.length - labelled)} onClick={() => setOnlyNoLabel(v => !v)} active={onlyNoLabel} />
        <KPI label="On the homepage carousel" value={fmtInt(carousel)} onClick={() => setOnlyCarousel(v => !v)} active={onlyCarousel} />
      </KPIRow>
      <Section title="Website labels & homepage carousel"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <FillLabelsButton rows={rows} onDone={(t, k) => (k === 'ok' ? ok(t) : fail(t))} />
            <Btn small onClick={sheet.reload}>{sheet.loading ? 'Refreshing…' : 'Refresh'}</Btn>
          </div>
        }>
        <p className="text-xs text-grayscale-1100 -mt-1">
          The <b>label</b> is the short name (“IIT Bombay”) used on the homepage carousel and as the university part of
          every page URL. The <b>carousel</b> switch decides whether it scrolls on the homepage — a label alone no
          longer puts it there. Same data as Universities → Global Universities; a change in either shows in both.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={q} onChange={setQ} wide placeholder="Filter by name, code, label, URL…" />
          <label className="inline-flex items-center gap-1.5 text-xs text-grayscale-1200 cursor-pointer whitespace-nowrap">
            <input type="checkbox" checked={onlyNoLabel} onChange={e => setOnlyNoLabel(e.target.checked)} /> Only without label
          </label>
          <label className="inline-flex items-center gap-1.5 text-xs text-grayscale-1200 cursor-pointer whitespace-nowrap">
            <input type="checkbox" checked={onlyCarousel} onChange={e => setOnlyCarousel(e.target.checked)} /> Only on carousel
          </label>
          <span className="text-xs text-grayscale-1100">{view.length === rows.length ? `${rows.length} universities` : `${view.length} of ${rows.length}`}</span>
        </div>
        <div className="overflow-auto rounded-lg border border-grayscale-300" style={{ maxHeight: '64vh' }}>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-grayscale-200 z-10">
              <tr className="text-left font-grotesk text-xs text-grayscale-1200">
                <th className="px-3 py-2">University</th>
                <th className="px-3 py-2">Website label</th>
                <th className="px-3 py-2">Carousel</th>
                <th className="px-3 py-2">Page URL</th>
                <th className="px-3 py-2 text-right">Techs</th>
              </tr>
            </thead>
            <tbody>
              {view.map(r => (
                <tr key={r.code} className="border-t border-grayscale-200 hover:bg-grayscale-100 align-top">
                  <td className="px-3 py-2">
                    <div className="font-medium text-grayscale-1300">{r.name}</div>
                    <div className="text-[11px] font-mono text-grayscale-1100">{r.code}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <InlineEdit value={r.website_label} warn muted={r.website_label_source === 'seed'}
                        title={r.website_label_source === 'seed' ? 'Carried over from the old landing page — click to make it official'
                          : r.website_label ? 'Click to edit' : 'No label — click to add'}
                        onSave={v => saveLabel(r.code, v)} />
                      {r.website_label_source === 'seed' && <span className="text-[10px] text-grayscale-1100">seed</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2"><CarouselToggle row={r} onSaved={ok} onError={fail} /></td>
                  <td className="px-3 py-2">
                    {r.page_path ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="font-mono text-[11px] text-grayscale-1200">{r.page_path}</span>
                        <OpenLink path={r.page_path} label="↗" />
                      </span>
                    ) : <span className="text-xs text-grayscale-1100" title="Appears after the next URL rebuild">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtInt(r.techs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!view.length && <div className="text-sm text-grayscale-1100 py-4 text-center">No university matches.</div>}
        </div>
      </Section>
    </div>
  );
};

export default LabelsCarousel;
